// Copyright (c) Ping. All rights reserved.
//
// "New Saved Message" modal — Figma `4219:102949`. An optional title /
// shortcut, a message body with a live character counter, and Cancel /
// Save-Message actions. The body is the REAL chat composer in `editorOnly`
// mode, so it carries the exact same rich formatting (bold/italic/lists/code/
// link via the on-selection toolbar) AND emoji picker — no re-implementation.
// Fully NativeWind-styled (RN primitives), works on web + react-native-macos.
// Persistence is owned by the caller: `onSave(title, body)`.

import React, { useEffect, useState } from "react";
import { Pressable, Text, TextInput, View } from "react-native";
import { Modal } from "../../components/Modal";
import { useColorScheme } from "../../foundations/colorScheme";
import { tokens } from "../../design-system/tokens";
import { SAVED_MESSAGE_MAX_LENGTH } from "./savedMessages";
import { ChatRoomComposerRich } from "../ChatRoomComposer";

export interface CreateSavedMessageModalProps {
  readonly visible: boolean;
  readonly onClose: () => void;
  /** Persist the entry. `title` is optional (empty string when blank). */
  readonly onSave: (title: string, body: string) => void;
  /** Optional seed for the body — e.g. the composer's current text. */
  readonly initialBody?: string;
}

// A no-op send — `editorOnly` hides the composer's send affordance, but the
// prop is required. Declared once so the composer's `onSend` identity is stable.
const NOOP_SEND = async (): Promise<void> => {};

export function CreateSavedMessageModal({
  visible,
  onClose,
  onSave,
  initialBody = "",
}: CreateSavedMessageModalProps): React.ReactElement {
  const scheme = useColorScheme();
  const colorScheme: "light" | "dark" = scheme === "dark" ? "dark" : "light";
  const colors = tokens.color.semantic[colorScheme];

  const [title, setTitle] = useState("");
  // Live markdown from the embedded composer (via `onComposerTextChange`).
  const [body, setBody] = useState(initialBody);

  // Re-seed each time the modal (re-)opens so a stale buffer never lingers and
  // a fresh `initialBody` is picked up. The composer is remounted per-open via
  // its `key` below so it reseeds from `initialText`.
  useEffect(() => {
    if (visible) {
      setTitle("");
      setBody(initialBody);
    }
  }, [visible, initialBody]);

  const trimmedBody = body.trim();
  const canSave = trimmedBody.length > 0;

  const handleSave = (): void => {
    if (!canSave) return;
    onSave(title.trim(), trimmedBody.slice(0, SAVED_MESSAGE_MAX_LENGTH));
    onClose();
  };

  return (
    <Modal visible={visible} onClose={onClose} accessibilityLabel="New saved message">
      <View className="w-[416px] max-w-full gap-6">
        {/* Header — Figma 24/extrabold. */}
        <Text className="font-sans text-[24px] font-extrabold text-light-text-primary dark:text-[#F5F6F8]">
          New Saved Message
        </Text>

        <View className="gap-5">
          {/* Title / shortcut (optional). */}
          <View className="gap-2">
            <Text className="font-sans text-[12px] text-light-text-secondary dark:text-[#6B7280]">
              Title / shortcut (optional)
            </Text>
            <TextInput
              value={title}
              onChangeText={setTitle}
              placeholder="e.g. follow-up"
              placeholderTextColor={colors.text.secondary}
              autoCapitalize="none"
              autoCorrect={false}
              className="rounded-[8px] bg-light-surface-secondary dark:bg-[#111318] px-3 py-3 font-sans text-[14px] text-light-text-primary dark:text-[#F5F6F8]"
              testID="saved-message-title-input"
            />
          </View>

          {/* Message body — the chat composer itself as a rich-text FIELD
              (format toolbar on selection + emoji), no send chrome. The
              composer's own input pill IS the field, so we DON'T wrap it in a
              second surface (that stranded the growing pill inside a tall empty
              box). It sits flush-left/right and grows with content, exactly
              like the chat composer. */}
          <View className="gap-1">
            <Text className="px-1 font-sans text-[12px] text-light-text-secondary dark:text-[#6B7280]">
              Message body
            </Text>
            <ChatRoomComposerRich
              // Remount per-open so it reseeds from `initialText` cleanly.
              key={visible ? "open" : "closed"}
              onSend={NOOP_SEND}
              editorOnly
              initialText={initialBody}
              placeholder="Type the message you want to save…"
              onComposerTextChange={setBody}
              pressEnterToSend={false}
              showAttachmentMenu={false}
              showVoiceRecording={false}
              showSchedule={false}
              showSavedMessages={false}
              showSlashCommands={false}
              showGifs={false}
              showStickers={false}
              denseComposerPadding
              // Round + clip the composer's own row so it reads as one
              // contained field in the modal (wrapper-only — the composer's
              // internal pill styling is untouched).
              containerClassName="rounded-[8px] overflow-hidden"
              containerContentClassName="px-[0px]! pt-[0px]! pb-[0px]! bg-transparent!"
              composerInputClassName="rounded-[8px]!"
              composerDarkBackgroundColor="#111318"
              composerLightBackgroundColor="#f5f5f7"
              testID="saved-message-body-composer"
            />
            <Text className="self-start px-1 font-sans text-[10px] text-light-text-secondary dark:text-[#6B7280]">
              {body.length} / {SAVED_MESSAGE_MAX_LENGTH}
            </Text>
          </View>
        </View>

        {/* Footer — Cancel + Save Message. */}
        <View className="flex-row items-center justify-end gap-4">
          <Pressable
            onPress={onClose}
            accessibilityRole="button"
            className="px-2 py-[10px] active:opacity-60"
            testID="saved-message-cancel"
          >
            <Text className="font-sans text-[14px] font-semibold text-light-text-secondary dark:text-[#6B7280]">
              Cancel
            </Text>
          </Pressable>
          <Pressable
            onPress={handleSave}
            disabled={!canSave}
            accessibilityRole="button"
            accessibilityState={{ disabled: !canSave }}
            // Indigo accent to match the composer's usual `#537EFF` action
            // color (not the Figma mock's one-off blue).
            className={`rounded-[8px] bg-light-accent-indigo dark:bg-dark-accent-indigo px-6 py-[12px] active:opacity-90 ${
              canSave ? "" : "opacity-50"
            }`}
            testID="saved-message-save"
          >
            <Text className="font-sans text-[14px] font-semibold text-white">
              Save Message
            </Text>
          </Pressable>
        </View>
      </View>
    </Modal>
  );
}
